import { HuaweiDownloadButton, LinkButton } from '@hg/stylizator/components'
import { isUndefined, media } from '@hg/stylizator/utils'
import * as React from 'react'
import { mobileVendor, mobileModel } from 'react-device-detect'
import { IWPGBlock } from 'react-gutenberg'
import styled from 'styled-components'

import { IThemeTypeData, ThemeTypeData } from 'app/blocks/sections/HomepageHero/models'
import ClientOnly from 'app/components/ClientOnly'
import HomepageHeroSection, { IDownloadPlatform } from 'app/components/CustomBlocks/Section/HomepageHeroSection'
import OsDownloadButton from 'app/components/OSDownloadButton'
import { ReactBlocks } from 'app/helpers/Blocks'
import useHasMounted from 'app/hooks/use-has-mounted'
import Android from 'app/icons/platform/Android'
import Apple from 'app/icons/platform/Apple'
import Docker from 'app/icons/platform/Docker'
import Macos from 'app/icons/platform/Macos'
import Windows from 'app/icons/platform/Windows'
import { useGlobalState } from 'app/store'

export interface IHomepageHeroAttributes {
  headingText: string
  themedHeadingText?: string
  subheadingText: string
  footerText?: string
  rightButtonText?: string
  rightButtonUrl?: string
  rightButtonClassName?: string
  illustration?: string
  illustrationWidth?: string
  illustrationHeight?: string
  illustrationAlt?: string
  leftButtonText: string
  leftButtonUrl: string
  leftButtonClassName?: string
  videoUrl?: string
  className?: string
  theme: keyof IThemeTypeData
  leftButtonOpenInNewTab?: boolean
  isDownloadEnabled?: boolean
  leftButtonWindowsText?: string
  leftButtonMacosText?: string
  leftButtonWindowsUrl?: string
  leftButtonWindows7Url?: string
  leftButtonMacosUrl?: string
  leftButtonAndroidText?: string
  leftButtonAndroidUrl?: string
  leftButtoniOSText?: string
  leftButtoniOSUrl?: string
  leftButtonDockerText?: string
  leftButtonDockerUrl?: string
  addDeviceModelTargeting?: boolean
  appGalleryDownloadUrl?: string
  androidDownloadUrl?: string
  infinixDownloadUrl?: string
}

export const getDownloadsList = (attr: IHomepageHeroAttributes): IDownloadPlatform[] => [
  {
    platform: 'Windows',
    icon: <Windows />,
    link: attr.leftButtonWindowsUrl ?? '',
    enabled: attr.leftButtonWindowsUrl !== '' && !isUndefined(attr.leftButtonWindowsUrl),
  },
  {
    platform: 'Android',
    icon: <Android />,
    link: attr.leftButtonAndroidUrl ?? '',
    enabled: attr.leftButtonAndroidUrl !== '' && !isUndefined(attr.leftButtonAndroidUrl),
  },
  {
    platform: 'MacOS',
    icon: <Macos />,
    link: attr.leftButtonMacosUrl ?? '',
    enabled: attr.leftButtonMacosUrl !== '' && !isUndefined(attr.leftButtonMacosUrl),
  },
  {
    platform: 'iOS',
    icon: <Apple />,
    link: attr.leftButtoniOSUrl ?? '',
    enabled: attr.leftButtoniOSUrl !== '' && !isUndefined(attr.leftButtoniOSUrl),
  },
  {
    platform: 'Docker',
    icon: <Docker />,
    link: attr.leftButtonDockerUrl ?? '',
    enabled: attr.leftButtonDockerUrl !== '' && !isUndefined(attr.leftButtonDockerUrl),
  },
]

const HomepageHeroSectionBlock: React.FC<IWPGBlock> = (props) => {
  const attributes = props.attrs as IHomepageHeroAttributes
  const [videoOpened, setVideoOpened] = React.useState<boolean>(false)
  const [blocks] = useGlobalState('reusableBlocks')
  const osButtonBlockData = blocks.get('honeygain-components/os-download-button')

  const theme = attributes.theme || 'default'
  const isHuawei = mobileVendor.toLowerCase() === 'huawei'
  const isInfinix = mobileVendor.toLowerCase() === 'infinix' || mobileModel.toLowerCase().includes('infinix')
  const hasMounted = useHasMounted()
  const buttonTheme = theme as keyof IThemeTypeData

  const renderLeftButton = () => {
    if (attributes.addDeviceModelTargeting && isHuawei) {
      return (
        <HuaweiDownloadButton
          className={`track-hero-huawei-download-button homepage-hero-left-button ${attributes.leftButtonClassName}`}
          href={attributes.appGalleryDownloadUrl}
          fullWidth={{ default: true, desktop: false }}
        />
      )
    }

    if (attributes.isDownloadEnabled) {
      return (
        <OsDownloadButton
          leftButtonWindowsText={attributes.leftButtonWindowsText ?? ''}
          leftButtonMacosText={attributes.leftButtonMacosText ?? ''}
          leftButtonAndroidText={attributes.leftButtonAndroidText ?? ''}
          leftButtonWindowsUrl={attributes.leftButtonWindowsUrl ?? ''}
          leftButtonWindows7Url={attributes.leftButtonWindows7Url ?? ''}
          leftButtonMacosUrl={attributes.leftButtonMacosUrl ?? ''}
          leftButtonAndroidUrl={attributes.leftButtonAndroidUrl ?? ''}
          leftButtoniOSText={attributes.leftButtoniOSText ?? ''}
          leftButtoniOSUrl={attributes.leftButtoniOSUrl ?? ''}
          skipUnknown
          fullWidth={{ default: true, desktop: false }}
          specialTheme={ThemeTypeData[buttonTheme].specialTheme}
          className="homepage-hero-left-button"
        />
      )
    }

    if (attributes.leftButtonText === 'none' ? undefined : attributes.leftButtonText && attributes.leftButtonUrl) {
      return (
        <LinkButton
          className="track-hero-left-button homepage-hero-left-button"
          type={ThemeTypeData[buttonTheme].leftButtonStyle}
          useCompact
          fullWidth={{ default: true, desktop: false }}
          href={attributes.leftButtonUrl}
          target={attributes.leftButtonOpenInNewTab ? '_blank' : undefined}
        >
          {attributes.leftButtonText}
        </LinkButton>
      )
    }

    return (
      <ClientOnly>
        <OsDownloadButton
          fullWidth={{ default: true, desktop: false }}
          specialTheme={ThemeTypeData[buttonTheme].specialTheme}
          {...(osButtonBlockData?.attrs ?? {})}
          className={`track-hero-download homepage-hero-left-button ${
            isInfinix ? 'track-hero-infinix-download-button' : ''
          } ${osButtonBlockData?.attrs?.className ?? ''}`}
          leftButtonAndroidUrl={
            isInfinix ? attributes.infinixDownloadUrl : osButtonBlockData?.attrs?.leftButtonAndroidUrl
          }
        />
      </ClientOnly>
    )
  }

  const renderRightButton = () => {
    if (attributes.addDeviceModelTargeting && isHuawei && hasMounted) {
      return (
        <LinkButton
          className="track-hero-android-download-button"
          type="secondary"
          useCompact
          href={attributes.androidDownloadUrl}
          fullWidth={{ default: true, desktop: false }}
        >
          Download app files
        </LinkButton>
      )
    }

    if (attributes.rightButtonText) {
      return (
        <LinkButton
          className={`track-hero-right-button ${attributes.rightButtonClassName ?? ''}`}
          type={ThemeTypeData[buttonTheme].rightButtonStyle}
          useCompact
          href={attributes.rightButtonUrl}
          fullWidth={{ default: true, desktop: false }}
        >
          {attributes.rightButtonText}
        </LinkButton>
      )
    }

    return null
  }

  return (
    <HomepageHeroSection
      className={`homepage-hero-section ${attributes.className}`}
      theme={theme}
      footerText={attributes?.footerText}
      headingText={attributes.headingText}
      themedHeadingText={attributes.themedHeadingText}
      subheadingText={attributes.subheadingText}
      illustration={
        attributes.illustration ? (
          <StyledHeroIllustration
            src={attributes.illustration}
            alt={attributes.illustrationAlt}
            width={attributes.illustrationWidth ?? '100%'}
            height={attributes.illustrationHeight ?? 'auto'}
            loading="lazy"
          />
        ) : undefined
      }
      leftButton={renderLeftButton()}
      rightButton={renderRightButton()}
      videoUrl={attributes.videoUrl}
      videoOpened={videoOpened}
      onVideoPlayClick={() => setVideoOpened(true)}
      onVideoCloseClick={() => setVideoOpened(false)}
      innerBlocks={<ReactBlocks innerBlocks={props.innerBlocks} />}
      isDownloadEnabled={attributes.isDownloadEnabled}
      downloads={getDownloadsList(attributes)}
      fullHeight
    />
  )
}

const StyledHeroIllustration = styled.img`
  @media ${media.desktop}, ${media.largeDesktop} {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }
`

export default HomepageHeroSectionBlock
